/**
 * L1: subscriptions whose daily spend spiked against their own trailing mean.
 *
 * Computed in the backend from the `cost_day` history already on disk — no
 * Cost Management call, and deliberately not `Microsoft.CostManagement/alerts`,
 * which stays empty until per-subscription anomaly rules are configured in the
 * portal. The point is to see a subscription double overnight before the
 * month-end invoice does the telling.
 *
 * The three states are kept apart, because they mean different things:
 *
 * * `anomalies_checked == null` — no history to judge from. Unknown.
 * * `anomalies_checked === 0` — history exists but no subscription has enough
 *   trailing days. Still unknown, and the caption says so rather than implying
 *   a clean bill of health.
 * * `anomalies_checked > 0` with an empty list — a measured "nothing spiked".
 */

import { Alert, DataGrid, EmptyState, Typography } from "@norican/react";

import type { CloudCost, CostAnomaly } from "@/lib/api";
import { formatCount, formatCurrency } from "@/lib/charts/config";

import { PanelCard } from "./panel-card";
import { formatDay } from "./status";

interface CostAnomalyPanelProps {
  cost: CloudCost;
}

export function CostAnomalyPanel({ cost }: CostAnomalyPanelProps) {
  const assessed = cost.anomalies_checked != null && cost.anomalies_checked > 0;

  if (!assessed) {
    return (
      <PanelCard title="Spend spikes" caption="Not yet assessable">
        <Typography variant="body-sm" tone="muted">
          {cost.anomaly_detail ||
            "No collected cost history to compare against yet, so no spike could be assessed — " +
              "which is not the same as no spike."}
        </Typography>
      </PanelCard>
    );
  }

  if (cost.anomalies.length === 0) {
    return (
      <PanelCard
        title="Spend spikes"
        caption={`${formatCount(cost.anomalies_checked)} subscription(s) assessed`}
      >
        <EmptyState size="sm" title="No subscription spiked" description={cost.anomaly_detail} />
      </PanelCard>
    );
  }

  return (
    <PanelCard
      title="Spend spikes"
      caption={`${formatCount(cost.anomalies.length)} of ${formatCount(
        cost.anomalies_checked,
      )} assessed subscription(s) spiked`}
      padding="md"
    >
      <div className="flex flex-col gap-4">
        <Alert tone="warning" title="Daily spend jumped against its own trailing mean">
          {cost.anomaly_detail}
        </Alert>

        <DataGrid<CostAnomaly>
          data={cost.anomalies}
          getRowId={(row) => `${row.subscription_id}-${row.day}`}
          density="compact"
          aria-label="Subscriptions whose daily spend spiked"
          columns={[
            {
              id: "subscription",
              header: "Subscription",
              accessor: (row) => row.name,
              sortable: true,
              cell: (row) => <span className="text-fg font-medium">{row.name}</span>,
            },
            {
              id: "day",
              header: "Day",
              accessor: (row) => row.day,
              sortable: true,
              cell: (row) => <span className="tabular-nums">{formatDay(row.day)}</span>,
            },
            {
              id: "daily",
              header: "That day",
              accessor: (row) => row.daily_cost,
              sortable: true,
              align: "end",
              cell: (row) => (
                <span className="text-fg font-medium tabular-nums">
                  {formatCurrency(row.daily_cost, row.currency)}
                </span>
              ),
            },
            {
              id: "baseline",
              header: "Trailing mean",
              accessor: (row) => row.baseline_mean,
              sortable: true,
              align: "end",
              cell: (row) => (
                <div className="flex flex-col items-end">
                  <span className="tabular-nums">
                    {formatCurrency(row.baseline_mean, row.currency)}
                  </span>
                  <span className="text-fg-muted text-xs">over {row.baseline_days} days</span>
                </div>
              ),
            },
            {
              id: "delta",
              header: "Increase",
              accessor: (row) => row.delta,
              sortable: true,
              align: "end",
              // A flagged row always cleared the floor, so the delta is positive.
              cell: (row) => (
                <span className="tabular-nums">+{formatCurrency(row.delta, row.currency)}</span>
              ),
            },
            {
              id: "ratio",
              header: "Multiple",
              accessor: (row) => row.ratio,
              sortable: true,
              align: "end",
              cell: (row) => (
                <span className="text-fg-muted text-sm tabular-nums">
                  {row.ratio == null ? "—" : `${row.ratio}x`}
                </span>
              ),
            },
          ]}
        />

        <Typography variant="body-sm" tone="muted">
          Day spend is derived from the month-to-date figures the cost sweep persists, so the month
          boundary is handled explicitly and a day the sweep missed is skipped rather than charged
          the whole gap. A subscription with no trailing mean shows an em dash for the multiple — it
          went from nothing to something, which has no meaningful ratio.
        </Typography>
      </div>
    </PanelCard>
  );
}
